// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type { AccomplishmentFormData } from '../types/schemas';

const ACCOMPLISHMENTS_QUERY_KEY = ['accomplishments'] as const;
const CREDITS_QUERY_KEY = ['credits'] as const;

/** Hook for fetching all accomplishments with optional filters. */
export function useAccomplishments(filters?: {
  search?: string;
  category?: string;
  experience_id?: string;
}) {
  return useQuery({
    queryKey: [...ACCOMPLISHMENTS_QUERY_KEY, filters],
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        '/api/v1/accomplishments',
        {
          params: {
            query: {
              search: filters?.search || undefined,
              category: filters?.category || undefined,
              experience_id: filters?.experience_id || undefined,
            },
          },
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
  });
}

/** Parse tags from comma-separated string. */
function parseTags(tagsStr: string | undefined): string[] | undefined {
  if (!tagsStr) return undefined;
  return tagsStr
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

/** Hook for creating a new accomplishment. */
export function useCreateAccomplishment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: AccomplishmentFormData) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/accomplishments',
        {
          body: {
            description: params.description,
            impact: params.impact || undefined,
            category: params.category || undefined,
            experience_id: params.experience_id || undefined,
            tags: parseTags(params.tags),
          },
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOMPLISHMENTS_QUERY_KEY });
    },
  });
}

/** Hook for updating an existing accomplishment. */
export function useUpdateAccomplishment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...params
    }: AccomplishmentFormData & { id: string }) => {
      const { data, error } = await apiClient.PUT(
        '/api/v1/accomplishments/{id}',
        {
          params: { path: { id } },
          body: {
            description: params.description,
            impact: params.impact || undefined,
            category: params.category || undefined,
            experience_id: params.experience_id || undefined,
            tags: parseTags(params.tags),
          },
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOMPLISHMENTS_QUERY_KEY });
    },
  });
}

/** Hook for AI-suggesting tags for an accomplishment. */
export function useSuggestAccomplishmentTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      description: string;
      impact?: string;
      category?: string;
      existing_tags?: string[];
    }) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/accomplishments/suggest-tags',
        {
          body: {
            description: params.description,
            impact: params.impact || undefined,
            category: params.category || undefined,
            existing_tags: params.existing_tags?.length
              ? params.existing_tags
              : undefined,
          },
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.tags;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
    },
  });
}

/** Hook for AI-polishing an accomplishment description.
 *  Supports two modes:
 *  - Edit mode: pass `id` of a saved accomplishment.
 *  - Create mode: pass `description` (and optional impact/category) to polish inline text.
 *    A placeholder ID is sent since the route requires one, but the backend ignores it. */
export function usePolishAccomplishment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      jobTitle,
      description,
      impact,
      category,
    }: {
      id?: string;
      jobTitle?: string;
      description?: string;
      impact?: string;
      category?: string;
    }) => {
      const pathId =
        id || '00000000-0000-0000-0000-000000000000';
      const body: Record<string, string> = {};
      if (jobTitle) body.job_title = jobTitle;
      if (description) body.description = description;
      if (impact) body.impact = impact;
      if (category) body.category = category;

      const { data, error } = await apiClient.POST(
        '/api/v1/accomplishments/{id}/polish',
        {
          params: { path: { id: pathId } },
          body,
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
    },
  });
}

/** Hook for deleting an accomplishment. */
export function useDeleteAccomplishment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await apiClient.DELETE(
        '/api/v1/accomplishments/{id}',
        { params: { path: { id } } },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOMPLISHMENTS_QUERY_KEY });
    },
  });
}

/** Hook for bulk deleting accomplishments. */
export function useBulkDeleteAccomplishments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/accomplishments/bulk-delete',
        { body: { ids } },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOMPLISHMENTS_QUERY_KEY });
    },
  });
}
